import { forwardRef, useEffect, useState } from "react";
import CrewLayout from "@/components/layouts/CrewLayout";
import { Platform, useWindowDimensions, View } from "react-native";
import { Button } from "@/components/ui/Button";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useTheme } from "@/hooks/useTheme";
import ThemedInput from "@/components/ThemedInput";
import * as Device from "expo-device";
import { useStorage } from "@/hooks/useStorage";

const LAYOUT_STORAGE_KEY = "crew_layout_preference";

const Layout = forwardRef<View>((props, ref) => {
  const { colors } = useTheme();
  const { getString, saveFast } = useStorage();

  // Viewport mode: 'web' = 16:9, 'mobile' = 9:16
  const [layout, setLayout] = useState<"mobile" | "web">(() => {
    const stored = getString(LAYOUT_STORAGE_KEY);
    return stored === "web" ? "web" : "mobile";
  });
  const [controlsHidden, setControlsHidden] = useState(false);

  const { width: winW, height: winH } = useWindowDimensions();
  const mobileLayout = Platform.OS !== "web" || Device.deviceType === Device.DeviceType.PHONE;

  // Animated viewport width/height
  const vw = useSharedValue(0);
  const vh = useSharedValue(0);

  // Compute and animate viewport
  useEffect(() => {
    const aspect = layout === "web" ? 16 / 9 : 9 / 16;
    const toolbarH = Platform.OS === "web" ? 64 : 0;
    const pad = 24;

    const availW = Math.max(0, winW - pad * 2);
    const availH = Math.max(0, winH - toolbarH - pad * 2);

    let w = Math.min(availW, availH * aspect);
    let h = w / aspect;

    if (layout === "mobile") {
      const cap = 430;
      if (w > cap) {
        w = cap;
        h = w / aspect;
      }
    }

    w = Math.max(280, Math.floor(w));
    h = Math.max(280, Math.floor(h));

    vw.value = withTiming(w, { duration: 200 });
    vh.value = withTiming(h, { duration: 200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winW, winH, layout]);

  const viewportStyle = useAnimatedStyle(() => ({
    width: vw.value,
    height: vh.value,
  }));

  return (
    <View ref={ref} style={{ flex: 1, backgroundColor: colors.backgroundSecondary }}>
      {/* Floating toolbar (web desktop only) */}
      {!mobileLayout && (
        <>
          {!controlsHidden && (
            <View
              style={{
                position: "absolute",
                top: 8,
                zIndex: 10,
                alignSelf: "center",
                left: "50%",
                transform: [{ translateX: "-50%" }],
              }}
              pointerEvents="box-none"
            >
              <View
                pointerEvents="auto"
                style={
                  {
                    gap: 8,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    flexDirection: "row",
                    alignItems: "center",
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: colors.border,
                    backgroundColor: colors.background,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
                  } as any
                }
              >
                <Button
                  style={{ height: 36, paddingHorizontal: 14, borderRadius: 999 }}
                  icon={layout === "web" ? "cellphone" : "monitor"}
                  text={layout === "web" ? "Mobile 9:16" : "Web 16:9"}
                  onPress={() => {
                    const newLayout = layout === "mobile" ? "web" : "mobile";
                    setLayout(newLayout);
                    saveFast(LAYOUT_STORAGE_KEY, newLayout);
                  }}
                />
                <ThemedInput
                  placeholder="Emulate User"
                  inputStyle={{
                    height: 36,
                    borderRadius: 999,
                    backgroundColor: colors.backgroundSecondary,
                    paddingHorizontal: 12,
                    minWidth: 220,
                  }}
                />
                <Button
                  style={{ height: 36, paddingHorizontal: 14, borderRadius: 999 }}
                  icon="eye-off"
                  text="Hide controls"
                  onPress={() => setControlsHidden(true)}
                />
              </View>
            </View>
          )}

          {controlsHidden && (
            <View
              style={{
                position: "absolute",
                top: 8,
                zIndex: 11,
                alignSelf: "center",
                left: "50%",
                transform: [{ translateX: "-50%" }],
              }}
              pointerEvents="box-none"
            >
              <Button
                pointerEvents="auto"
                style={{ height: 32, paddingHorizontal: 12, borderRadius: 999, backgroundColor: colors.background }}
                icon="eye"
                text="Show controls"
                onPress={() => setControlsHidden(false)}
              />
            </View>
          )}
        </>
      )}

      {/* Centered 16:9 / 9:16 viewport */}
      {!mobileLayout ? (
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <Animated.View
            style={[
              viewportStyle,
              {
                overflow: "hidden",
                borderRadius: 18,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: colors.backgroundSecondary,
                boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.2)",
                elevation: 10,
              },
            ]}
          >
            <CrewLayout {...props} />
          </Animated.View>
        </View>
      ) : (
        <CrewLayout {...props} />
      )}
    </View>
  );
});

Layout.displayName = "Layout";
export default Layout;
